import type { DashboardGridAncestor } from '../../models/builder.types'
import type { DashboardRow, DashboardTab } from '../../models/layout.types'

import { parseLayout, serializeLayout } from './serialize'

function clone(rows: readonly DashboardRow[]): DashboardRow[] {
  return parseLayout(serializeLayout(rows))
}

/**
 * Cross-grid moves. v3 let vuedraggable share one `group` per level (`sections-group`,
 * `col-group`, `tab-group`), so a block could leave its tab and a tab could leave its strip — but
 * the move mutated two reactive arrays in two component instances, and a drop into a descendant
 * of the dragged node was never refused. Here a move is one pure transform over the ROOT rows,
 * addressed by ancestor paths, and a move into the moved node's own subtree is a no-op.
 */
export interface RowMoveSource {
  ancestors: readonly DashboardGridAncestor[]
  rowId: string
}

export interface RowMoveTarget {
  ancestors: readonly DashboardGridAncestor[]
  /** Insertion index in the target grid, counted BEFORE the moved row is taken out. */
  index: number
}

export interface TabMoveSource {
  ancestors: readonly DashboardGridAncestor[]
  rowId: string
  cellId: string
  tabId: string
}

export interface TabMoveTarget {
  ancestors: readonly DashboardGridAncestor[]
  rowId: string
  cellId: string
  /** Insertion index in the target strip, counted BEFORE the moved tab is taken out. */
  index: number
}

function sameStep(left: DashboardGridAncestor, right: DashboardGridAncestor): boolean {
  return left.rowId === right.rowId && left.cellId === right.cellId && left.tabId === right.tabId
}

export function samePath(
  left: readonly DashboardGridAncestor[],
  right: readonly DashboardGridAncestor[],
): boolean {
  return left.length === right.length && left.every((step, index) => sameStep(step, right[index]!))
}

/** The step `target` takes right after leaving `base`, or null when it never passes through it. */
function stepBelow(
  target: readonly DashboardGridAncestor[],
  base: readonly DashboardGridAncestor[],
): DashboardGridAncestor | null {
  if (target.length <= base.length) return null
  return samePath(target.slice(0, base.length), base) ? (target[base.length] ?? null) : null
}

/** A section cannot be dropped into one of its own tabs. */
export function canMoveRow(source: RowMoveSource, target: RowMoveTarget): boolean {
  return stepBelow(target.ancestors, source.ancestors)?.rowId !== source.rowId
}

/** A tab cannot be dropped into a strip that lives inside that same tab. */
export function canMoveTab(source: TabMoveSource, target: TabMoveTarget): boolean {
  const below = stepBelow(target.ancestors, source.ancestors)
  return !(
    below &&
    below.rowId === source.rowId &&
    below.cellId === source.cellId &&
    below.tabId === source.tabId
  )
}

/** A tab whose persisted `sections` is missing must gain the array before it can hold a row. */
function tabWithRows(tab: DashboardTab, rows: readonly DashboardRow[]): DashboardTab {
  const source = Array.isArray(tab.source.sections) ? tab.source : { ...tab.source, sections: [] }
  return { ...tab, rows, source }
}

function updateGrid(
  rows: readonly DashboardRow[],
  ancestors: readonly DashboardGridAncestor[],
  update: (rows: readonly DashboardRow[]) => readonly DashboardRow[],
): readonly DashboardRow[] {
  const [step, ...rest] = ancestors
  if (!step) return update(rows)
  return rows.map((row) =>
    row.id !== step.rowId
      ? row
      : {
          ...row,
          cells: row.cells.map((cell) =>
            cell.id !== step.cellId
              ? cell
              : {
                  ...cell,
                  tabs: cell.tabs.map((tab) =>
                    tab.id === step.tabId
                      ? tabWithRows(tab, updateGrid(tab.rows, rest, update))
                      : tab,
                  ),
                },
          ),
        },
  )
}

function gridAt(
  rows: readonly DashboardRow[],
  ancestors: readonly DashboardGridAncestor[],
): readonly DashboardRow[] | null {
  let current = rows
  for (const step of ancestors) {
    const cell = current
      .find((row) => row.id === step.rowId)
      ?.cells.find((c) => c.id === step.cellId)
    const tab = cell?.tabs.find((entry) => entry.id === step.tabId)
    if (!tab) return null
    current = tab.rows
  }
  return current
}

function updateCellTabs(
  rows: readonly DashboardRow[],
  ancestors: readonly DashboardGridAncestor[],
  rowId: string,
  cellId: string,
  update: (tabs: readonly DashboardTab[]) => readonly DashboardTab[],
): readonly DashboardRow[] {
  return updateGrid(rows, ancestors, (grid) =>
    grid.map((row) =>
      row.id !== rowId
        ? row
        : {
            ...row,
            cells: row.cells.map((cell) => {
              if (cell.id !== cellId) return cell
              const source = Array.isArray(cell.source.tabs)
                ? cell.source
                : { ...cell.source, tabs: [] }
              return { ...cell, tabs: update(cell.tabs), source }
            }),
          },
    ),
  )
}

function insertAt<T>(items: readonly T[], index: number, item: T): T[] {
  const at = Math.max(0, Math.min(index, items.length))
  return [...items.slice(0, at), item, ...items.slice(at)]
}

export function moveRow(
  rows: readonly DashboardRow[],
  source: RowMoveSource,
  target: RowMoveTarget,
): DashboardRow[] {
  const sourceGrid = gridAt(rows, source.ancestors)
  const from = sourceGrid?.findIndex((row) => row.id === source.rowId) ?? -1
  const moved = sourceGrid?.[from]
  if (!moved || !gridAt(rows, target.ancestors) || !canMoveRow(source, target)) return clone(rows)

  if (samePath(source.ancestors, target.ancestors)) {
    const to = target.index > from ? target.index - 1 : target.index
    if (from === to) return clone(rows)
    return clone(
      updateGrid(rows, source.ancestors, (grid) =>
        insertAt(
          grid.filter((row) => row !== moved),
          to,
          moved,
        ),
      ),
    )
  }
  const removed = updateGrid(rows, source.ancestors, (grid) => grid.filter((row) => row !== moved))
  return clone(updateGrid(removed, target.ancestors, (grid) => insertAt(grid, target.index, moved)))
}

export function moveTab(
  rows: readonly DashboardRow[],
  source: TabMoveSource,
  target: TabMoveTarget,
): DashboardRow[] {
  const cellOf = (ancestors: readonly DashboardGridAncestor[], rowId: string, cellId: string) =>
    gridAt(rows, ancestors)
      ?.find((row) => row.id === rowId)
      ?.cells.find((cell) => cell.id === cellId)
  const sourceCell = cellOf(source.ancestors, source.rowId, source.cellId)
  const from = sourceCell?.tabs.findIndex((tab) => tab.id === source.tabId) ?? -1
  const moved = sourceCell?.tabs[from]
  const targetCell = cellOf(target.ancestors, target.rowId, target.cellId)
  if (!moved || !targetCell || !canMoveTab(source, target)) return clone(rows)

  if (targetCell === sourceCell) {
    const to = target.index > from ? target.index - 1 : target.index
    if (from === to) return clone(rows)
    return clone(
      updateCellTabs(rows, source.ancestors, source.rowId, source.cellId, (tabs) =>
        insertAt(
          tabs.filter((tab) => tab !== moved),
          to,
          moved,
        ),
      ),
    )
  }
  const removed = updateCellTabs(rows, source.ancestors, source.rowId, source.cellId, (tabs) =>
    tabs.filter((tab) => tab !== moved),
  )
  return clone(
    updateCellTabs(removed, target.ancestors, target.rowId, target.cellId, (tabs) =>
      insertAt(tabs, target.index, moved),
    ),
  )
}
